import React, { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { QRCodeCanvas } from 'qrcode.react'
import {
	Backpack,
	AlertTriangle,
	BuildingSkyscraper,
	Calendar,
	CalendarEvent,
	ChevronRight,
	Clock,
	ExternalLink,
	Headset,
	Help,
	InfoCircle,
	Link as LinkIcon,
	Luggage,
	MapPin,
	Ticket,
	User,
	Users,
	X,
} from 'tabler-icons-react'
import {
	formatCheckInTimes,
	formatCustomFieldValue,
	formatDate,
	formatTripRange,
	getContrastTextColor,
	getSafeColor,
	getTravelerStationLabel,
	getTripStatus,
	resolveTravelerExperienceDates,
} from './myExperienceUtils'
import { getWarningResolutionHref } from './warningUtils'
import {
	getEmbeddedFrameSource,
	openEmbeddedPageInNewTab,
} from './embeddedPageUtils'
import ExperiencePreviewBar from '../components/ExperiencePreviewBar'
import useExperiencePreview from '../hooks/useExperiencePreview'
import './MyExperiencePage.css'

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001'
const DEFAULT_ACCENT = '#228be6'

const ICONS = {
	trip: Luggage,
	schedule: Calendar,
	hotel: BuildingSkyscraper,
	packing: Backpack,
	faq: Help,
	contact: Headset,
	chat: Users,
	info: InfoCircle,
	link: LinkIcon,
}

const MyExperiencePage = ({ experiencePreview = false }) => {
	const { checkInId, experienceId } = useParams()
	const [travelerData, setTravelerData] = useState()
	const [activeEmbeddedPage, setActiveEmbeddedPage] = useState(null)
	const {
		data: experiencePreviewData,
		previewOptions,
		selectedStationId,
		selectedSegmentId,
		setStationId,
		setSegmentId,
	} = useExperiencePreview(experienceId, experiencePreview)
	const data = experiencePreview ? experiencePreviewData : travelerData

	useEffect(() => {
		if (experiencePreview) return undefined

		const controller = new AbortController()
		setTravelerData(undefined)

		fetch(`${BASE_URL}/api/myexperience/${checkInId}`, {
			signal: controller.signal,
		})
			.then(async (response) => {
				if (!response.ok) throw new Error('Experience lookup failed')
				return response.json()
			})
			.then(setTravelerData)
			.catch((error) => {
				if (error.name !== 'AbortError') setTravelerData(null)
			})

		return () => controller.abort()
	}, [checkInId, experiencePreview])

	useEffect(() => {
		if (!activeEmbeddedPage) return undefined

		const previousOverflow = document.body.style.overflow
		const handleKeyDown = (event) => {
			if (event.key === 'Escape') setActiveEmbeddedPage(null)
		}

		document.body.style.overflow = 'hidden'
		window.addEventListener('keydown', handleKeyDown)
		return () => {
			document.body.style.overflow = previousOverflow
			window.removeEventListener('keydown', handleKeyDown)
		}
	}, [activeEmbeddedPage])

	const view = useMemo(() => {
		if (!data) return null
		const experience = data.experience || {}
		const checkIn = data.checkIn || {}
		const { checkInDate, checkOutDate } = resolveTravelerExperienceDates(
			checkIn,
			experience,
		)
		const accent = getSafeColor(experience.primaryColor, DEFAULT_ACCENT)
		const stationName =
			typeof checkIn.station?.name === 'string'
				? checkIn.station.name.trim()
				: ''
		const stationAddress =
			stationName && typeof checkIn.station?.address === 'string'
				? checkIn.station.address.trim()
				: ''

		return {
			experience,
			checkIn,
			travelerName:
				typeof checkIn.travelerName === 'string'
					? checkIn.travelerName.trim()
					: '',
			checkInDate,
			checkOutDate,
			accent,
			title: experience.shortName || experience.name || 'Your Experience',
			location: experience.location?.name || stationName || 'Your experience',
			stationName,
			stationAddress,
			dateRange: formatTripRange(checkInDate, checkOutDate),
			checkInDateLabel: formatDate(checkInDate) || 'To be announced',
			checkInTimes: formatCheckInTimes(experience.checkInTimes),
			status: getTripStatus(checkInDate, checkOutDate),
			links: Array.isArray(experience.links) ? experience.links : [],
			embeddedPages: Array.isArray(experience.embeddedPages)
				? experience.embeddedPages
				: [],
		}
	}, [data])

	const previewBar = experiencePreview ? (
		<ExperiencePreviewBar
			stations={previewOptions.stations || []}
			segments={previewOptions.segments || []}
			selectedStationId={selectedStationId}
			selectedSegmentId={selectedSegmentId}
			onStationChange={setStationId}
			onSegmentChange={setSegmentId}
		/>
	) : null

	if (data === undefined) {
		return (
			<>
				{previewBar}
				<div className='myexp-state'>
					<div className='myexp-loader' aria-hidden='true' />
					<p>Loading your experience…</p>
				</div>
			</>
		)
	}

	if (data === null || !view) {
		return (
			<>
				{previewBar}
				<div className='myexp-state'>
					<div className='myexp-state-card'>
						<InfoCircle size={36} />
						<h1>Experience not found</h1>
						<p>
							This traveler link may be invalid. Check the link or contact your
							trip organizer for help.
						</p>
					</div>
				</div>
			</>
		)
	}

	const { experience, checkIn, checkInDateLabel, checkInTimes, accent } = view
	const address = view.stationAddress
	const customFields = Object.entries(
		checkIn.customFields &&
			typeof checkIn.customFields === 'object' &&
			!Array.isArray(checkIn.customFields)
			? checkIn.customFields
			: {},
	)
		.map(([label, value]) => ({ label, value: formatCustomFieldValue(value) }))
		.filter((field) => field.value)
	const resolvableWarnings = (
		Array.isArray(checkIn.warnings) ? checkIn.warnings : []
	).filter((warning) => getWarningResolutionHref(warning?.resolutionLink))
	const mapUrl = address
		? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
		: null
	const headerImage =
		experience.travelerPageHeaderImage || experience.stripImage || null
	const heroStyle = headerImage
		? { backgroundImage: `url("${headerImage.replace(/"/g, '%22')}")` }
		: {
				backgroundImage: `linear-gradient(135deg, ${accent}, #09090b)`,
			}
	const activeFrameSource = activeEmbeddedPage
		? getEmbeddedFrameSource(activeEmbeddedPage.source)
		: null
	const previewExperienceId = experiencePreview ? experienceId : null
	const previewQuery = new URLSearchParams()
	if (selectedStationId) {
		previewQuery.set('stationId', selectedStationId)
	}
	if (selectedSegmentId) {
		previewQuery.set('segmentId', selectedSegmentId)
	}
	const passHref = previewExperienceId
		? `/get-pass/preview/${previewExperienceId}${
				previewQuery.toString() ? `?${previewQuery.toString()}` : ''
			}`
		: `/get-pass/${checkIn.id}`
	const openEmbeddedPage = (item) => {
		const frameSource = getEmbeddedFrameSource(item?.source)
		if (openEmbeddedPageInNewTab(frameSource)) return

		setActiveEmbeddedPage(item)
	}

	return (
		<>
			{previewBar}
			<div
				className='myexp-page'
				style={{
					'--myexp-accent': accent,
					'--myexp-accent-text': getContrastTextColor(accent),
				}}
			>
			<div className='myexp-shell'>
				<header className='myexp-hero' style={heroStyle}>
					<div className='myexp-hero-overlay' />
					<div className='myexp-topbar'>
						<div className='myexp-brand'>
							{experience.org?.logo ? (
								<img
									src={experience.org.logo}
									alt={experience.org.name || 'Organizer'}
								/>
							) : (
								<span>LH</span>
							)}
						</div>
						<p>CURRENT EXPERIENCE</p>
						<div className='myexp-topbar-spacer' aria-hidden='true' />
					</div>

					<div className='myexp-hero-content'>
						<div>
							<h1>{view.title}</h1>
							<p className='myexp-location'>{view.location}</p>
							<p className='myexp-dates'>{view.dateRange}</p>
							<span className='myexp-status'>{view.status}</span>
						</div>
					</div>
				</header>

				<main className='myexp-content'>
					{resolvableWarnings.length > 0 && (
						<section
							className='myexp-card myexp-warnings-card'
							aria-labelledby='traveler-warnings-title'
						>
							<h2 id='traveler-warnings-title'>ACTION NEEDED</h2>
							<div className='myexp-card-body myexp-warnings-list'>
								{resolvableWarnings.map((warning, index) => {
									const resolutionHref = getWarningResolutionHref(
										warning?.resolutionLink,
									)
									return (
										<div
											className='myexp-warning'
											key={`${warning?.title || 'warning'}-${index}`}
										>
											<AlertTriangle aria-hidden='true' />
											<div>
												<div className='myexp-warning-copy'>
													<strong>{warning?.title || 'Action required'}</strong>
													<small>
														This warning will not clear automatically after you
														resolve it. Please allow up to 48 hours for your status
														to be updated.
													</small>
												</div>
												{resolutionHref && (
													<a
														className='myexp-button myexp-button-primary'
														href={resolutionHref}
														target='_blank'
														rel='noopener noreferrer'
													>
														<ExternalLink size={18} />
														RESOLVE NOW
													</a>
												)}
											</div>
										</div>
									)
								})}
							</div>
						</section>
					)}

					<section
						className='myexp-card myexp-checkin-card'
						aria-labelledby='check-in-info-title'
					>
						<h2 id='check-in-info-title'>CHECK-IN INFO</h2>
						<div className='myexp-card-body myexp-checkin-body'>
							<div
								className={`myexp-info-grid${
									view.travelerName || checkInTimes.length
										? ''
										: ' myexp-info-grid-single'
								}`}
							>
								{view.travelerName && (
									<div className='myexp-info-item'>
										<User />
										<div>
											<span>Traveler</span>
											<strong>{view.travelerName}</strong>
										</div>
									</div>
								)}
								<div className='myexp-info-item'>
									<CalendarEvent />
									<div>
										<span>Check-in Date</span>
										<strong>{checkInDateLabel}</strong>
									</div>
								</div>
								{checkInTimes.length > 0 && (
									<div className='myexp-info-item'>
										<Clock />
										<div>
											<span>Check-in Time</span>
											<strong>
												{checkInTimes.map((time) => (
													<span className='myexp-time-line' key={time}>
														{time}
													</span>
												))}
											</strong>
										</div>
									</div>
								)}
							</div>

							{customFields.length > 0 && (
								<div className='myexp-custom-fields-grid'>
									{customFields.map((field) => (
										<div className='myexp-info-item' key={field.label}>
											<InfoCircle />
											<div>
												<span>{field.label}</span>
												<strong>{field.value}</strong>
											</div>
										</div>
									))}
								</div>
							)}

							{view.stationName && (
								<div className='myexp-location-row'>
									<MapPin />
									<div>
										<span>
											{getTravelerStationLabel(experience.stationLabelOverride)}
										</span>
										<strong>{view.stationName}</strong>
										{address && <p>{address}</p>}
									</div>
								</div>
							)}

							{mapUrl && (
								<div className='myexp-actions'>
									<a
										className='myexp-button myexp-button-primary'
										href={mapUrl}
										target='_blank'
										rel='noreferrer'
									>
										<ExternalLink size={20} />
										VIEW ON MAP
									</a>
								</div>
							)}
						</div>
					</section>

					<section
						className='myexp-card myexp-pass-card'
						aria-labelledby='your-pass-title'
					>
						<h2 id='your-pass-title'>YOUR PASS</h2>
						<div className='myexp-card-body myexp-pass-row'>
							<div
								className='myexp-pass-preview'
								style={{
									backgroundColor: getSafeColor(
										experience.primaryColor,
										DEFAULT_ACCENT,
									),
									color: getSafeColor(experience.textColor, '#ffffff'),
								}}
							>
								<small>{view.title}</small>
								<b>PASS</b>
								<span>{checkIn.travelerName}</span>
								<div className='myexp-mini-qr'>
									<QRCodeCanvas value={checkIn.id} size={48} level='M' />
								</div>
							</div>
							<div className='myexp-pass-copy'>
								<h3>Your Check-In Pass</h3>
								<p>Use your pass to check in and access your experience.</p>
								<Link
									to={passHref}
									className='myexp-button myexp-button-secondary'
								>
									<Ticket size={20} />
									VIEW PASS
								</Link>
							</div>
							<ChevronRight className='myexp-pass-chevron' aria-hidden='true' />
						</div>
					</section>

					{view.embeddedPages.length > 0 && (
						<section
							className='myexp-feature-pages'
							aria-labelledby='embedded-pages-title'
						>
							<div className='myexp-feature-pages-grid'>
								{view.embeddedPages.map((item, index) => {
									const Icon = ICONS[item.icon] || LinkIcon
									return (
										<button
											key={`${item.label}-${index}`}
											type='button'
											onClick={() => openEmbeddedPage(item)}
										>
											<span className='myexp-feature-page-icon'>
												<Icon />
											</span>
											<span className='myexp-feature-page-copy'>
												<strong>{item.label}</strong>
												<small>
													{item.details?.trim() || `View ${item.label}`}
												</small>
											</span>
											<ChevronRight
												className='myexp-feature-page-chevron'
												aria-hidden='true'
											/>
										</button>
									)
								})}
							</div>
						</section>
					)}

					{view.links.length > 0 && (
						<section
							className='myexp-card myexp-links-card'
							aria-labelledby='useful-links-title'
						>
							<h2 id='useful-links-title'>USEFUL LINKS</h2>
							<div className='myexp-links-grid'>
								{view.links.map((item, index) => {
									const Icon = ICONS[item.icon] || LinkIcon
									return (
										<a
											key={`${item.label}-${index}`}
											href={item.url}
											target='_blank'
											rel='noreferrer'
										>
											<Icon />
											<span>{item.label}</span>
										</a>
									)
								})}
							</div>
						</section>
					)}
				</main>
			</div>

			{activeEmbeddedPage && activeFrameSource && (
				<div
					className='myexp-embed-overlay'
					role='dialog'
					aria-modal='true'
					aria-labelledby='myexp-embed-title'
				>
					<div className='myexp-embed-dialog'>
						<header>
							<h2 id='myexp-embed-title'>{activeEmbeddedPage.label}</h2>
							<button
								type='button'
								onClick={() => setActiveEmbeddedPage(null)}
								aria-label={`Close ${activeEmbeddedPage.label}`}
							>
								<X />
							</button>
						</header>
						<iframe
							{...activeFrameSource}
							title={activeEmbeddedPage.label}
							sandbox={
								activeFrameSource.src
									? 'allow-scripts allow-same-origin allow-forms allow-presentation'
									: 'allow-scripts allow-forms allow-presentation'
							}
							allow='clipboard-write; encrypted-media; fullscreen; picture-in-picture'
							allowFullScreen
						/>
					</div>
				</div>
			)}
			</div>
		</>
	)
}

export default MyExperiencePage
